import React, { PropsWithChildren, ReactNode } from 'react';
import { Form, Formik } from 'formik';
import { ProfileUser, Retailer } from '@ordermentum/ui-types';
import classNames from 'classnames';
import { NOTIFICATION_DELAY } from '@ordermentum/ui-constants';
import { ToastReduxAction } from '@ordermentum/ui-definitions';
import Button from '../../Button/Button';
import Banner from '../../Banner';
import Divider from '../../Divider';
import { Checkbox } from '../../Input/Checkbox';
import { Input } from '../../../atoms/Input';
import { directDebitFormSchema } from '../../../utilities/formSchemas';
import PurchaserToggleBlock from './PurchaserToggleBlock';
import { DirectDebitParams } from '../../../redux/payment_methods';
import BSBInput from './BSBInput';

const CTA_POSITIONS = {
  FIXED: 'fixed',
  STICKY: 'sticky',
};

type DirectDebitRequestType = PropsWithChildren<{
  user?: ProfileUser;
  isPaymentLink?: boolean;
  retailer?: Retailer;
  retailerId: string;
  title?: string;
  CTAPosition?: 'fixed' | 'sticky';
  hideCancel?: boolean;
  purchaserId?: string;
  onFailedValidation: (toast: ToastReduxAction) => Promise<void>;
  onInviteSignatoryClick?: () => void;
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => void;
  onSuccess?: (paymentMethodId: string) => void;
  onCancel?: () => void;
}>;

type SectionType = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

const Section = (props: SectionType) => {
  const { title, subtitle, children } = props;

  return (
    <section aria-labelledby={title} className="flex flex-col gap-l">
      <div className="flex flex-col gap-xxs">
        <h4 className="font-heading text-h4 text-strong">{title}</h4>
        {subtitle && (
          <p className="font-body text-body-small text-regular">{subtitle}</p>
        )}
      </div>
      <div>{children}</div>
    </section>
  );
};

type FieldInformationType = {
  label: string;
  text?: string;
};

const FieldInformation = (props: FieldInformationType) => {
  const { label, text } = props;
  return (
    <div className="flex max-w-72 flex-col gap-xxs tablet-min:w-full">
      <label className="font-body text-label-small-bold text-strong">
        {label}
      </label>
      <p className="line-clamp-2 text-ellipsis font-body  text-body text-regular">
        {text || '-'}
      </p>
    </div>
  );
};

const InputRow = ({ children }: { children: ReactNode }) => (
  <div className="flex grow flex-col gap-xxs">{children}</div>
);

type FormValues = {
  acknowledgement: boolean;
  firstSignatory: string;
  accountName: string;
  bsb: string;
  accountNumber: string;
  defaultAll: boolean;
  email: string;
  phone: string;
};

export const DirectDebitRequest = (props: DirectDebitRequestType) => {
  const {
    user,
    retailer,
    retailerId,
    CTAPosition = CTA_POSITIONS.FIXED,
    hideCancel = false,
    isPaymentLink = false,
    title,
    purchaserId,
    addDirectPayment,
    onCancel,
    onInviteSignatoryClick,
    onSuccess,
    onFailedValidation,
  } = props;

  // user and retailer can be undefined (ex: payment link)
  const tradingName = retailer?.tradingName;
  const address = retailer?.billingAddress?.formatted;
  const abn = retailer?.abn;
  const fullName = user?.fullName;
  const email = user?.email;
  const phone = user?.phone;

  const handleSubmit = async (values: FormValues, actions) => {
    const payload = {
      account: values.accountNumber,
      accountName: values.accountName,
      bsb: values.bsb,
      country: 'AUS',
      currency: 'AUD',
      defaultPayment: true,
      defaultAll: values.defaultAll,
      type: 'bank',
      terms: values.acknowledgement,
      userId: retailerId,
      purchaserId,
    };

    if (!values.phone || !abn) {
      onFailedValidation({
        type: 'DIRECT_DEBIT__VALIDATION_FAILED__TOAST',
        notificationType: 'error',
        notificationPayload: `You're missing some required fields. Please provide all required information.`,
        notificationDelay: NOTIFICATION_DELAY,
      });

      return;
    }

    try {
      const res = await addDirectPayment(payload, retailerId);
      actions.resetForm();
      if (onSuccess) {
        // @ts-expect-error the action does return the response
        onSuccess(res.value.data.id, {
          name: values.firstSignatory,
          email: values.email,
          phone: values.phone,
          retailerId,
        });
      }
    } catch {
      actions.setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-l">
      {title && <h2 className="font-heading text-h2 text-strong">{title}</h2>}
      {onInviteSignatoryClick && (
        <Banner
          variant="info"
          title="Set up your bank account to pay invoices."
          actions={{
            alignment: 'start',
            items: [
              <Button
                key="invite-signatory"
                variant="text-link"
                expandOnMobile={false}
                onClick={onInviteSignatoryClick}
              >
                Invite authorised signatory
              </Button>,
            ],
          }}
        >
          Direct debit request must be submitted by an authorised signatory of
          your business bank account. If this is not you, please invite them to
          submit this request.
        </Banner>
      )}
      <Formik
        validationSchema={directDebitFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          editable: isPaymentLink,
          firstSignatory: user?.fullName ?? '',
          accountName: '',
          bsb: '',
          accountNumber: '',
          acknowledgement: false,
          email: user?.email ?? '',
          phone: user?.phone ?? '',
          defaultAll: isPaymentLink,
        }}
        onSubmit={handleSubmit}
      >
        {({ values, errors, setFieldValue, isSubmitting, resetForm }) => (
          <Form className="flex flex-col gap-l">
            <Section
              title="Business details"
              subtitle={
                !isPaymentLink
                  ? 'If you would like to make changes to these details, please go to Business Details in Settings.'
                  : ''
              }
            >
              <div className="flex flex-wrap gap-m">
                <FieldInformation label="Business name" text={tradingName} />
                <FieldInformation label="Billing address" text={address} />
                <FieldInformation label="ABN" text={abn} />
              </div>
            </Section>
            <Divider type="subtle" />
            <Section title="Account details">
              <div className="flex gap-m tablet-min:flex-col tablet-max:flex-wrap">
                <InputRow>
                  <Input
                    isFuturo
                    tag="accountName"
                    inputLabel="Account name"
                    hasError={!!errors.accountName}
                    errorLabel={errors.accountName}
                    size="max"
                    value={values.accountName}
                    onChange={setFieldValue}
                    onBlur={setFieldValue}
                  />
                </InputRow>
                <InputRow>
                  <BSBInput
                    value={values.bsb}
                    error={errors.bsb}
                    onChange={setFieldValue}
                  />
                </InputRow>
                <InputRow>
                  <Input
                    isFuturo
                    tag="accountNumber"
                    inputLabel="Account number"
                    hasError={!!errors.accountNumber}
                    errorLabel={errors.accountNumber}
                    size="max"
                    value={values.accountNumber}
                    onChange={setFieldValue}
                    onBlur={setFieldValue}
                  />
                </InputRow>
              </div>
            </Section>
            <Divider type="subtle" />
            <Section
              title="Contact details"
              subtitle={
                !isPaymentLink
                  ? 'If you would like to make changes to these details, please go to User Details in Settings.'
                  : ''
              }
            >
              <div
                className={classNames('flex gap-xs', {
                  'flex-col': isPaymentLink,
                  'flex-wrap': !isPaymentLink,
                })}
              >
                {isPaymentLink ? (
                  <>
                    <InputRow>
                      <Input
                        isFuturo
                        tag="firstSignatory"
                        inputLabel="Authorised signatory"
                        hasError={!!errors.firstSignatory}
                        errorLabel={errors.firstSignatory}
                        size="max"
                        value={values.firstSignatory}
                        onChange={setFieldValue}
                        onBlur={setFieldValue}
                      />
                    </InputRow>
                    <div className="flex gap-m">
                      <InputRow>
                        <Input
                          isFuturo
                          tag="email"
                          inputLabel="Email"
                          hasError={!!errors.email}
                          errorLabel={errors.email}
                          size="max"
                          value={values.email}
                          onChange={setFieldValue}
                          onBlur={setFieldValue}
                        />
                      </InputRow>
                      <InputRow>
                        <Input
                          isFuturo
                          tag="phone"
                          inputLabel="Phone"
                          hasError={!!errors.phone}
                          errorLabel={errors.phone}
                          size="max"
                          value={values.phone}
                          onChange={setFieldValue}
                          onBlur={setFieldValue}
                        />
                      </InputRow>
                    </div>
                  </>
                ) : (
                  <>
                    <FieldInformation
                      label="Authorised signatory"
                      text={fullName}
                    />
                    <FieldInformation label="Email" text={email} />
                    <FieldInformation label="Phone" text={phone} />
                  </>
                )}
              </div>
            </Section>
            <Divider type="subtle" />
            {!isPaymentLink && (
              <PurchaserToggleBlock
                type="direct"
                allowToggleAll
                formikFunction={checked => {
                  setFieldValue('defaultAll', checked);
                }}
                formikValue={values.defaultAll}
              />
            )}
            <Checkbox
              name="acknowledgement"
              onChange={val => {
                setFieldValue('acknowledgement', val);
              }}
            >
              <div className="flex flex-col">
                <p className="text-justify text-body text-regular">
                  I, {fullName ?? '[user name]'}, by checking this box, accept
                  the{' '}
                  <Button
                    as="a"
                    expandOnMobile={false}
                    variant="tertiary"
                    href="https://static.ordermentum.com/mobile/direct-debit-terms.html"
                  >
                    Ordermentum Direct Debit Agreement.
                  </Button>{' '}
                  I also confirm that:
                </p>
                <ul className="list-disc pl-xl text-justify">
                  <li>
                    <p className="text-body text-regular">
                      I am authorised to sign on behalf of the Business, I am
                      authorised to operate the Business&apos;s bank account and
                      I am hereby signing in accordance with the account
                      authority on the account;
                    </p>
                  </li>
                  <li>
                    <p className="text-body text-regular">
                      I request and authorise Zai Australia Pty Ltd (Zai), User
                      ID’s 342203 and 481561, as agent for Ordermentum Pty Ltd
                      (ACN 600 457 412) the provider of the online ordering
                      platform at www.ordermentum.com (the Platform), to arrange
                      a debit to my nominated account to pay for each order that
                      my business has submitted via the Platform for goods and
                      or services, where I have indicated Direct Debit as the
                      preferred payment option for that order / related supplier
                      invoice;
                    </p>
                  </li>
                  <li>
                    <p className="text-body text-regular">
                      This debit or charge will be arranged by Zai’s financial
                      institution and made through the Bulk Electronic Clearing
                      System Framework (BECS) from the nominated account and
                      will be subject to the terms and conditions of the Direct
                      Debit Request Service Agreement.
                    </p>
                  </li>
                </ul>
              </div>
            </Checkbox>

            <div
              className={classNames({
                'bg-regular fixed tablet-max:w-full tablet-max:max-w-full tablet-max:left-0 max-w-screen-md w-1/2 transition-bottom duration-1000':
                  CTAPosition === CTA_POSITIONS.STICKY,
                'flex flex-col gap-l': CTAPosition === CTA_POSITIONS.FIXED,
                'bottom-0': values.acknowledgement,
                '-bottom-44': !values.acknowledgement,
              })}
            >
              <Divider type="subtle" />
              <div
                className={classNames(
                  'flex justify-end gap-xxs tablet-max:flex-col-reverse',
                  {
                    'pt-xl pb-xxxl px-xl': CTAPosition === CTA_POSITIONS.STICKY,
                  }
                )}
              >
                {!hideCancel && (
                  <Button
                    variant="secondary"
                    type="reset"
                    onClick={() => {
                      resetForm();

                      if (onCancel) {
                        onCancel();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!values.acknowledgement}
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default DirectDebitRequest;
